import React, { useEffect, useMemo, useState } from "react";
import SearchInput from "./shared/SearchInput";
import CustomSelect from "./shared/CustomSelect";
import { fetchPartnerSubbies, updatePartnerSubbie } from "../utils/api";
import { cssUrl } from "../utils/cssVersion";

const filterTemplateStyles = cssUrl("filterTemplate.css");

/* ============================================================================
 * SubbieEditorModal
 * ----------------------------------------------------------------------------
 * Opened from the "Subbie Editor" icon in SubcontractorComplete. Lists every
 * partner Subbie record (with its record id). Selecting one opens an editor
 * for its five text fields — Subbie (default), Name, Email, Phone, Surname —
 * chosen via a CustomSelect. Each field is edited as separate entries (add /
 * change / remove a line); a preview shows what all five will save as.
 * Save writes every field the user changed. SPID is never editable here: a
 * cleaner is only ever linked from their own Subbie field on
 * SubcontractorComplete.
 *
 * Storage format: Subbie joins with " & ", the other four with ", ".
 *
 * Props:
 *   records  – final-subbie records (used to show SPID ids as cleaner names)
 *   onSaved  – ({ id, name, subbieNames, ...fields, updated }) => void
 *   onClose  – () => void
 * ==========================================================================*/

const S = {
  backdrop: {
    position: "fixed", inset: 0, top: "var(--fr-modal-top, 0px)", background: "rgba(2,6,23,0.7)", zIndex: 1100,
    display: "flex", alignItems: "flex-start", justifyContent: "center",
    padding: "40px 16px", overflowY: "auto",
  },
  modal: {
    position: "relative", maxHeight: "calc(var(--fr-modal-max, 82vh) - 76px)", overflowY: "auto", overscrollBehavior: "contain", width: "100%", maxWidth: 920,
    background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
    border: "1px solid rgba(34,211,238,0.25)", borderRadius: 16,
    padding: "18px 18px 14px",
  },
  panelAccent: {
    position: "absolute", top: 0, left: 0, right: 0, height: 3,
    background: "linear-gradient(90deg, #22d3ee, #3b82f6, #8b5cf6)",
  },
  head: { position: "sticky", top: 0, zIndex: 5, background: "#1e293b", boxShadow: "0 -18px 0 0 #1e293b",  display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 },
  title: { margin: 0, fontSize: "1rem", fontWeight: 700, color: "#f1f5f9" },
  closeX: { background: "none", border: "none", color: "rgba(255,255,255,0.6)", fontSize: "1.4rem", lineHeight: 1, cursor: "pointer" },
  eyebrow: { fontSize: "0.62rem", letterSpacing: "0.14em", textTransform: "uppercase", color: "#22d3ee", fontWeight: 700 },
  card: { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: 12, marginTop: 14 },
  colHead: { fontSize: "0.62rem", letterSpacing: "0.05em", textTransform: "uppercase", color: "rgba(255,255,255,0.45)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  rowName: { fontSize: "0.85rem", fontWeight: 700, color: "#22d3ee", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  cell: { fontSize: "0.84rem", color: "#e2e8f0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  mono: { fontSize: "0.72rem", color: "rgba(255,255,255,0.45)", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" },
  empty: { padding: 24, textAlign: "center", color: "rgba(255,255,255,0.5)", fontStyle: "italic", fontSize: "0.85rem" },
  nameRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 8 },
  nameInput: {
    flex: 1, minWidth: 0, padding: "9px 12px", borderRadius: 8,
    border: "1px solid rgba(34,211,238,0.25)", background: "rgba(255,255,255,0.04)",
    color: "#e2e8f0", fontSize: "0.88rem", outline: "none",
  },
  removeBtn: {
    width: 32, height: 32, borderRadius: 8, flexShrink: 0, cursor: "pointer",
    border: "1px solid rgba(239,68,68,0.3)", background: "rgba(239,68,68,0.1)", color: "#f87171",
    display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1rem",
  },
  addBtn: {
    display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 999,
    border: "1px solid rgba(34,197,94,0.35)", background: "rgba(34,197,94,0.12)", color: "#4ade80",
    fontSize: "0.78rem", fontWeight: 700, cursor: "pointer",
  },
  chip: {
    display: "inline-block", padding: "3px 10px", borderRadius: 999, fontSize: "0.72rem", fontWeight: 600,
    background: "rgba(34,211,238,0.1)", border: "1px solid rgba(34,211,238,0.3)", color: "#22d3ee",
  },
  footer: { display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 14 },
  cancelBtn: {
    background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.7)", border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 10, padding: "10px 18px", fontSize: "0.85rem", fontWeight: 600, cursor: "pointer",
  },
  saveBtn: (disabled) => ({
    background: "linear-gradient(135deg, #22d3ee 0%, #3b82f6 100%)", color: "#0f172a", border: "none",
    borderRadius: 10, padding: "10px 22px", fontSize: "0.85rem", fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
  }),
  error: {
    marginTop: 10, padding: "8px 12px", borderRadius: 8, background: "rgba(239,68,68,0.1)",
    border: "1px solid rgba(239,68,68,0.3)", color: "#fca5a5", fontSize: "0.8rem",
  },
  closeBtn: {
    width: "100%", padding: 11, borderRadius: 10, border: "none", background: "rgba(255,255,255,0.06)",
    color: "#e2e8f0", fontWeight: 600, cursor: "pointer", marginTop: 12,
  },
};

const TABLE_COLS = "1.6fr 1.2fr 0.6fr 1fr";

// Editable text fields: key -> label, split on, join with, placeholder
const FIELD_DEFS = [
  { key: "subbie",  label: "Subbie",  sep: "&", join: " & ", placeholder: "e.g. JN Bolo" },
  { key: "name",    label: "Name",    sep: ",", join: ", ",  placeholder: "e.g. Joshua Nino" },
  { key: "email",   label: "Email",   sep: ",", join: ", ",  placeholder: "e.g. joshua@gmail.com" },
  { key: "phone",   label: "Phone",   sep: ",", join: ", ",  placeholder: "(042) 409-0020", maxLength: 14 },
  { key: "surname", label: "Surname", sep: ",", join: ", ",  placeholder: "e.g. Bolo" },
];
const FIELD_BY_KEY = Object.fromEntries(FIELD_DEFS.map((f) => [f.key, f]));
const FIELD_LABELS = FIELD_DEFS.map((f) => f.label);
const keyForLabel = (label) => FIELD_DEFS.find((f) => f.label === label)?.key ?? "subbie";

// (042) 409-0020 — same rule as UsersManagement's mobile field
const formatPhone = (v) => {
  const d = (v ?? "").toString().replace(/\D/g, "").slice(0, 10);
  if (d.length === 0) return "";
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

// Stored phones may be in the old "Joshua -(040) 637-3274" shape — reduce each
// to just the number. Entries without 10 digits are left as typed.
const normalisePhone = (v) => {
  const d = (v ?? "").toString().replace(/\D/g, "");
  return d.length === 10 ? formatPhone(d) : (v ?? "").toString().trim();
};

const splitEntries = (text, sep) =>
  (text ?? "").toString().split(sep).map((p) => p.trim()).filter(Boolean);

// Editor drafts — phone entries are normalised on load so every remaining
// entry saves in (042) 409-0020 form.
const draftsFrom = (partner) =>
  Object.fromEntries(
    FIELD_DEFS.map((f) => {
      const parts = splitEntries(partner?.[f.key], f.sep);
      return [f.key, f.key === "phone" ? parts.map(normalisePhone) : parts];
    })
  );

const joined = (entries, def) => entries.map((e) => e.trim()).filter(Boolean).join(def.join);

const SubbieEditorModal = ({ records, onSaved, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [partners, setPartners] = useState([]);
  const [query, setQuery] = useState("");

  const [selectedId, setSelectedId] = useState(null);
  const [drafts, setDrafts] = useState(null);      // { subbie: [...], name: [...], ... }
  const [editField, setEditField] = useState("subbie");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = filterTemplateStyles;
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  useEffect(() => {
    const handleKey = (e) => e.key === "Escape" && !saving && onClose();
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [saving, onClose]);

  useEffect(() => {
    (async () => {
      const res = await fetchPartnerSubbies();
      if (res.status === "success") setPartners(res.data ?? []);
      else setLoadError(res.error || "Failed to load Subbie records");
      setLoading(false);
    })();
  }, []);

  // cleaner record id -> CleanerID name (for the SPID chips)
  const cleanerName = useMemo(() => {
    const m = new Map();
    (records ?? []).forEach((r) => m.set(r.recordId, r.name));
    return m;
  }, [records]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().replace(/\s+/g, "");
    if (!q) return partners;
    return partners.filter((p) =>
      `${p.subbie} ${p.name} ${p.surname} ${p.id}`.toLowerCase().replace(/\s+/g, "").includes(q)
    );
  }, [partners, query]);

  const selected = partners.find((p) => p.id === selectedId) ?? null;

  const select = (p) => {
    if (saving) return;
    setSelectedId(p.id);
    setDrafts(draftsFrom(p));
    setEditField("subbie");   // Subbie is always the default field on click
    setError(null);
  };

  const activeDef = FIELD_BY_KEY[editField];
  const entries = drafts?.[editField] ?? [];

  const updateEntries = (fn) =>
    setDrafts((d) => ({ ...d, [editField]: fn(d?.[editField] ?? []) }));
  const setEntry = (i, v) =>
    updateEntries((n) =>
      n.map((x, idx) => (idx === i ? (editField === "phone" ? formatPhone(v) : v) : x))
    );
  const removeEntry = (i) => updateEntries((n) => n.filter((_, idx) => idx !== i));
  const addEntry = () => updateEntries((n) => [...n, ""]);

  // Which fields differ from what's stored (compared on the joined text)
  const changedKeys = useMemo(() => {
    if (!selected || !drafts) return [];
    return FIELD_DEFS.filter(
      (f) => joined(drafts[f.key] ?? [], f) !== joined(splitEntries(selected[f.key], f.sep), f)
    ).map((f) => f.key);
  }, [selected, drafts]);

  const subbieEmpty = !!drafts && joined(drafts.subbie ?? [], FIELD_BY_KEY.subbie) === "";
  const canSave = !!selected && changedKeys.length > 0 && !subbieEmpty && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      // Only send the fields the user changed — untouched columns keep their exact text
      const payload = Object.fromEntries(
        changedKeys.map((k) => [k, (drafts[k] ?? []).map((e) => e.trim()).filter(Boolean)])
      );
      const res = await updatePartnerSubbie(selected.id, payload);
      if (res.status !== "success") throw new Error(res.error || "Update failed");
      const data = res.data;
      const merged = { ...(data.fields ?? {}), subbieNames: data.subbieNames ?? [] };
      setPartners((prev) => prev.map((p) => (p.id === data.id ? { ...p, ...merged } : p)));
      setDrafts(draftsFrom({ ...selected, ...merged }));
      onSaved(data);
    } catch (err) {
      setError(err.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={S.backdrop} onClick={() => !saving && onClose()}>
      <style>{`
        .se-row { transition: background .12s ease; }
        .se-row:hover { background: rgba(34,211,238,0.07); }
      `}</style>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <div style={S.panelAccent} />

        <div style={S.head}>
          <h4 style={S.title}>Subbie Editor</h4>
          <button type="button" style={S.closeX} onClick={onClose} aria-label="Close">×</button>
        </div>

        <div style={{ marginBottom: 14 }}>
          <SearchInput value={query} onChange={setQuery} placeholder="Search subbie, name or record id…" />
        </div>

        <div style={{ ...S.eyebrow, margin: "0 2px 8px" }}>
          Subbie records · {filtered.length}{query ? ` of ${partners.length}` : ""}
        </div>

        {loading ? (
          <div style={S.empty}>Loading Subbie records…</div>
        ) : loadError ? (
          <div style={{ ...S.empty, color: "#fca5a5" }}>{loadError}</div>
        ) : filtered.length === 0 ? (
          <div style={S.empty}>No Subbie records match.</div>
        ) : (
          <div style={{ maxHeight: 260, overflowY: "auto", overflowX: "auto" }}>
            <div style={{ minWidth: 620 }}>
              <div
                style={{
                  display: "grid", gridTemplateColumns: TABLE_COLS, alignItems: "end",
                  padding: "8px 8px 10px", borderBottom: "1px solid rgba(255,255,255,0.12)",
                  columnGap: 8, position: "sticky", top: 0, zIndex: 1, background: "#1b2537",
                }}
              >
                <span style={S.colHead}>Subbie</span>
                <span style={S.colHead}>Name</span>
                <span style={S.colHead}>Linked</span>
                <span style={S.colHead}>Record ID</span>
              </div>
              {filtered.map((p, i) => (
                <div
                  key={p.id}
                  className="se-row"
                  onClick={() => select(p)}
                  style={{
                    display: "grid", gridTemplateColumns: TABLE_COLS, alignItems: "center",
                    padding: "9px 8px", cursor: "pointer", borderRadius: 8, columnGap: 8,
                    borderTop: i ? "1px solid rgba(255,255,255,0.06)" : "none",
                    background: p.id === selectedId ? "rgba(34,211,238,0.12)" : undefined,
                  }}
                  title={p.subbie}
                >
                  <span style={S.rowName}>{p.subbie || "—"}</span>
                  <span style={S.cell} title={p.name}>{p.name || "—"}</span>
                  <span style={S.cell}>{p.spid.length} cleaner{p.spid.length === 1 ? "" : "s"}</span>
                  <span style={S.mono}>{p.id}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {selected && drafts && (
          <div style={S.card}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 10 }}>
              <span style={S.eyebrow}>Edit {selected.subbie || "subbie"}</span>
              <span style={S.mono}>{selected.id}</span>
            </div>

            {selected.spidOnly && (
              <div
                style={{
                  marginBottom: 12, padding: "8px 12px", borderRadius: 8,
                  background: "rgba(167,139,250,0.08)", border: "1px solid rgba(167,139,250,0.3)",
                  color: "#c4b5fd", fontSize: "0.78rem", lineHeight: 1.4,
                }}
              >
                Company subbie — linking a cleaner only updates SPID, so Name, Email, Phone and
                Surname are maintained here.
              </div>
            )}

            {/* Which text field is being edited — Subbie by default */}
            <div style={{ maxWidth: 280, marginBottom: 12 }}>
              <CustomSelect
                label="Field"
                value={activeDef.label}
                options={FIELD_LABELS}
                onChange={(label) => setEditField(keyForLabel(label))}
              />
            </div>

            {/* One line per entry in the chosen field */}
            {entries.length === 0 && (
              <div style={{ ...S.empty, padding: "10px 0 14px", textAlign: "left" }}>
                No {activeDef.label.toLowerCase()} entries yet — add one below.
              </div>
            )}
            {entries.map((n, i) => (
              <div key={`${editField}:${i}`} style={S.nameRow}>
                <input
                  style={S.nameInput}
                  value={n}
                  onChange={(e) => setEntry(i, e.target.value)}
                  placeholder={activeDef.placeholder}
                  maxLength={activeDef.maxLength}
                  disabled={saving}
                  autoFocus={i === entries.length - 1 && n === ""}
                />
                <button
                  type="button"
                  style={S.removeBtn}
                  onClick={() => removeEntry(i)}
                  disabled={saving}
                  aria-label={`Remove ${activeDef.label} entry`}
                  title="Remove this entry"
                >
                  ×
                </button>
              </div>
            ))}

            <button type="button" style={S.addBtn} onClick={addEntry} disabled={saving}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
              </svg>
              Add {activeDef.label.toLowerCase()}
            </button>

            {/* What every field will save as — changed ones highlighted */}
            <div style={{ marginTop: 14 }}>
              <div style={{ ...S.eyebrow, color: "rgba(255,255,255,0.45)", marginBottom: 6 }}>
                Will save as
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "84px minmax(0,1fr)", rowGap: 6, columnGap: 10 }}>
                {FIELD_DEFS.map((f) => {
                  const changed = changedKeys.includes(f.key);
                  const value = joined(drafts[f.key] ?? [], f);
                  return (
                    <React.Fragment key={f.key}>
                      <button
                        type="button"
                        onClick={() => setEditField(f.key)}
                        disabled={saving}
                        title={`Edit ${f.label}`}
                        style={{
                          textAlign: "left", background: "none", border: "none", padding: 0, cursor: "pointer",
                          fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase",
                          color: f.key === editField ? "#22d3ee" : "rgba(255,255,255,0.5)",
                        }}
                      >
                        {f.label}
                        {changed && <span style={{ color: "#fbbf24" }}> •</span>}
                      </button>
                      <span
                        style={{
                          fontSize: "0.84rem", lineHeight: 1.4, wordBreak: "break-word",
                          color: changed ? "#fbbf24" : "#e2e8f0", fontWeight: changed ? 600 : 400,
                        }}
                      >
                        {value || <span style={{ color: "rgba(255,255,255,0.35)", fontStyle: "italic" }}>(empty)</span>}
                      </span>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            {selected.spid.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <div style={{ ...S.eyebrow, color: "rgba(255,255,255,0.45)", marginBottom: 6 }}>
                  Linked cleaners (SPID) — read-only, changed only from the cleaner's Subbie field
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {selected.spid.map((id) => (
                    <span key={id} style={S.chip} title={id}>
                      {cleanerName.get(id) ?? id}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {subbieEmpty && (
              <div style={S.error}>Subbie needs at least one name before you can save.</div>
            )}
            {error && <div style={S.error}>{error}</div>}

            <div style={S.footer}>
              <button
                type="button"
                style={S.cancelBtn}
                onClick={() => select(selected)}
                disabled={saving || changedKeys.length === 0}
                title="Discard all changes"
              >
                Reset
              </button>
              <button type="button" style={S.saveBtn(!canSave)} onClick={handleSave} disabled={!canSave}>
                {saving
                  ? "Saving…"
                  : changedKeys.length > 1
                  ? `Save ${changedKeys.length} fields`
                  : "Save"}
              </button>
            </div>
          </div>
        )}

        <button type="button" style={S.closeBtn} onClick={onClose} disabled={saving}>
          Close
        </button>
      </div>
    </div>
  );
};

export default SubbieEditorModal;